import React from 'react';
import { format } from 'date-fns';
import { twMerge } from 'tailwind-merge';
import { getPlaceholderAvatarSrc } from '@components/main-components/avatar/utils';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import blurImage from '@components/main-components/image/blur-image';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { HighlightedCollectorFragmentDoc } from '@graphql/reactQuery';
import {
  type AudienceSongInfoFragment,
  getFragment,
  gql,
  HighlightedAudienceFragmentDoc,
  type HighlightedCollectorFragment,
} from '@graphql/reactQuery';
import { formatWeiToEthForDisplay } from '@utils/helpers';
import { webappUriPath } from '@utils/url';
import { HIGHLIGHTED } from './constants';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment HighlightedCollector on ReleaseCollector {
    id
    volumeSpent
    firstNftCollectedDate
    user {
      username
      webappUri
      publicAddress
      avatar {
        id
        url
      }
    }
  }

  fragment HighlightedAudience on Release {
    id
    earliestCollector {
      id
      ...HighlightedCollector
    }
    topSpentCollector {
      id
      ...HighlightedCollector
    }
    latestCollector {
      id
      ...HighlightedCollector
    }
  }
`);

function Highlight({
  type,
  collector,
}: {
  type: 'WHALE' | 'RECENT' | 'EARLIEST';
  collector: HighlightedCollectorFragment;
}) {
  const volume = formatWeiToEthForDisplay({ weiValue: collector.volumeSpent, maxDecimals: 4 });
  const date = format(new Date(collector.firstNftCollectedDate), 'MMM dd, yyyy');

  const metadata = type === 'WHALE' ? `Spent ${volume} ETH` : `Collected ${date}`;

  return (
    <AccessibleLink
      pathnameUri={webappUriPath(collector.user.webappUri)}
      className="group relative col-span-6 flex w-full min-w-0 flex-col gap-0 self-stretch overflow-hidden rounded-xl md:rounded-[20px]"
    >
      <div className="aspect-square w-full overflow-hidden rounded-t-xl md:rounded-t-[20px]">
        <NextFutureImage
          src={
            collector.user.avatar?.url ??
            getPlaceholderAvatarSrc({ walletAddress: collector.user.publicAddress })
          }
          alt="user avatar"
          className="aspect-square rounded-t-xl object-cover transition-transform duration-300 ease-linear [transform:translateZ(0)] group-hover:scale-[1.2] md:rounded-t-[20px]"
          placeholder="blur"
          blurDataURL={blurImage(400, 400)}
          width={400}
          height={400}
        />
      </div>

      <div className="absolute right-[2px] top-[2px] flex items-center gap-1 rounded-[93px] bg-[rgba(0,0,0,0.60)] py-[3px] pl-3 pr-[3px] md:right-1 md:top-1 md:py-[6px] md:pr-[6px]">
        <span className="text-title hidden font-title text-title-xxs font-medium text-white md:block lt:hidden xl:block">
          {HIGHLIGHTED[type].long_title}
        </span>
        <span className="text-title font-title text-title-xxs font-medium text-white md:hidden lt:block xl:hidden">
          {HIGHLIGHTED[type].short_title}
        </span>

        <NextFutureImage width={22} height={22} alt="badge" src={HIGHLIGHTED[type].icon} />
      </div>

      <div className="absolute bottom-[55px] left-0 right-0 hidden h-[25%] bg-[linear-gradient(180deg,rgba(14,18,19,0.00)_0%,#0E1213_100%)] md:flex lt:hidden xl:flex" />

      <div className="hidden h-[55px] flex-col gap-1 bg-black px-4 pb-4 pt-0 md:flex lt:hidden xl:flex">
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-title text-title-s font-medium text-base100">
          {collector.user.username}
        </span>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-s font-normal text-base400">
          {metadata}
        </span>
      </div>
    </AccessibleLink>
  );
}

function EmptyHighlight({ type }: { type: 'WHALE' | 'RECENT' | 'EARLIEST' }) {
  const { icon, long_description, long_title, short_descrition, short_title } = HIGHLIGHTED[type];

  return (
    <div
      className={twMerge(
        'col-span-6 flex aspect-square h-auto w-full min-w-0 items-center justify-center',
        'self-stretch rounded-xl bg-base100 p-3 md:aspect-[5/6] md:rounded-[20px] md:p-10 lt:aspect-square lt:p-3 xl:aspect-[5/6] xl:p-10',
      )}
    >
      <div className="flex flex-col items-center gap-1 text-center md:gap-3 lt:gap-1 xl:gap-3">
        <NextFutureImage src={icon} alt="badge" height={28} width={28} />
        <div className="flex flex-col gap-1 md:gap-2">
          <span className="hidden font-title text-title-m font-medium text-base900 md:block lt:hidden xl:block">
            {long_title}
          </span>
          <span className="block font-title text-title-xxs font-medium text-base900 md:hidden lt:block xl:hidden">
            {short_title}
          </span>
          <span className="hidden font-base text-base-m font-normal text-base500 md:block lt:hidden xl:block">
            {long_description}
          </span>
          <span className="block font-base text-base-xs font-normal text-base500 md:hidden lt:block xl:hidden">
            {short_descrition}
          </span>
        </div>
      </div>
    </div>
  );
}

export function HighlightedAudience({
  releaseFrag,
  hasAudience,
}: {
  releaseFrag: AudienceSongInfoFragment;
  hasAudience: boolean;
}) {
  const collector = getFragment(HighlightedAudienceFragmentDoc, releaseFrag);

  const whaleFrag = getFragment(HighlightedCollectorFragmentDoc, collector.topSpentCollector);
  const recentFrag = getFragment(HighlightedCollectorFragmentDoc, collector.latestCollector);
  const earliestFrag = getFragment(HighlightedCollectorFragmentDoc, collector.earliestCollector);

  return (
    <div className="mb-8 mt-4 grid w-full grid-cols-[repeat(18,1fr)] gap-2 overflow-visible md:gap-3 lt:gap-2 xl:gap-3">
      {hasAudience ? (
        <>
          {!!whaleFrag ? (
            <Highlight type="WHALE" collector={whaleFrag} />
          ) : (
            <EmptyHighlight type="WHALE" />
          )}
          {!!recentFrag ? (
            <Highlight type="RECENT" collector={recentFrag} />
          ) : (
            <EmptyHighlight type="RECENT" />
          )}
          {!!earliestFrag ? (
            <Highlight type="EARLIEST" collector={earliestFrag} />
          ) : (
            <EmptyHighlight type="EARLIEST" />
          )}
        </>
      ) : (
        <>
          <EmptyHighlight type="WHALE" />
          <EmptyHighlight type="RECENT" />
          <EmptyHighlight type="EARLIEST" />
        </>
      )}
    </div>
  );
}
